/**
 * Ventanas de escritura de **Gestión → Compras**.
 *
 * Portan las pantallas del panel anterior:
 * - `menu/compra/new_create.php` + `Compra::guardarCompra` → **registrar compra**.
 * - `menu/compra/new_payment.php` + `Compra::registrar_pago` → **pagar al
 *   proveedor**, repartiendo el importe entre varias compras.
 * - las notas de **crédito** y **débito** de la compra (`crear_nota_credito`,
 *   `crear_nota_debito`).
 *
 * Reglas conservadas:
 * - No se repite la **serie y número** de una compra activa.
 * - El importe del reparto no puede pasarse del **pendiente** de cada compra
 *   (el original lo permitía y dejaba saldos negativos).
 * - Una nota de **crédito** no supera el total de las líneas de la compra; la de
 *   débito no tiene tope.
 * - El stock **no** se mueve al registrar la compra: lo hace la **entrada** de
 *   Inventario, que es la que marca `compra_producto.stocked_purchase`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { fechaCorta, hoyIso, monto } from '../gestion-formato';
import {
  useAnularNota,
  useAnularPago,
  useActualizarCompra,
  useCatalogosCompra,
  useCompra,
  useGuardarNota,
  useNotasDeCompra,
  usePagosDeProveedor,
  useRegistrarCompra,
  useRegistrarPago,
  useResumenPagos,
  useSubirComprobanteCompra,
} from '../compras.api';
import type {
  AfectacionPago,
  CompraPorPagar,
  LineaCompraEntrada,
  ProductoCompra,
} from '../compras.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface AvisoProps {
  onAviso: (mensaje: string) => void;
  onClose: () => void;
}

/** Fila editable de una línea de compra. */
interface LineaEditable extends LineaCompraEntrada {
  clave: number;
}

const CONTROL_INLINE =
  'h-8 w-24 rounded-control border border-ink-200 bg-white px-2 text-right text-[13px] text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Línea editable de una compra ya registrada (con su id de `compra_producto`). */
interface LineaEditableCompra {
  id: number;
  producto_id: number;
  producto: string;
  cantidad: number;
  basico: number;
  dscto: number;
  isc: number;
  rod: number;
}

/** Convierte las líneas del detalle en filas editables. */
function lineasDeDetalle(productos: ProductoCompra[]): LineaEditableCompra[] {
  return productos.map((item) => ({
    id: item.id,
    producto_id: item.producto_id,
    producto: item.producto,
    cantidad: item.cantidad,
    basico: item.basico,
    dscto: item.dscto,
    isc: item.isc,
    rod: item.rod,
  }));
}

/** Totales de la compra a partir de sus líneas (IGV 18% y percepción 1%). */
function calcularTotales(
  lineas: LineaEditableCompra[],
  afectaIgv: boolean,
  afectaPercepcion: boolean,
  totalFise: string,
) {
  let igv = 0;
  let importe = 0;

  for (const linea of lineas) {
    const valorVenta = (linea.basico - linea.dscto + linea.isc + linea.rod) * linea.cantidad;
    const igvLinea = afectaIgv ? valorVenta * 0.18 : 0;

    igv += igvLinea;
    importe += valorVenta + igvLinea;
  }

  const fise = Number(totalFise) || 0;
  const importeTotal = importe + fise;
  const percepcion = afectaPercepcion ? importeTotal * 0.01 : 0;

  return { igv, importe, fise, importeTotal, percepcion, totalAPagar: importeTotal + percepcion };
}

/* ------------------------------------------------------------------ *
 * Registrar compra
 * ------------------------------------------------------------------ */

export function CompraNuevaDialog({ abierto, onAviso, onClose }: AvisoProps & { abierto: boolean }) {
  const catalogos = useCatalogosCompra(abierto);
  const registrar = useRegistrarCompra();

  const [cabecera, setCabecera] = useState({
    tipo_comprobante_id: 2,
    serie: '',
    numero: '',
    proveedor_id: 0,
    fecha_emision: hoyIso(),
    fecha_vencimiento: '',
    moneda: 'SOLES' as 'SOLES' | 'DOLARES',
    tipo_cambio: 1,
    orden_compra: '',
    guia: '',
    scop: '',
  });
  const [lineas, setLineas] = useState<LineaEditable[]>([
    {
      clave: 1,
      producto_id: 0,
      cantidad: 1,
      basico: 0,
      dscto: 0,
      isc: 0,
      rod: 0,
      precio_unidad: 0,
      igv: 0,
      valor_venta: 0,
    },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [avisoConfig, setAvisoConfig] = useState<string | null>(null);
  const [afectaIgv, setAfectaIgv] = useState(true);
  const [afectaPercepcion, setAfectaPercepcion] = useState(true);
  const [porcentajePercepcion, setPorcentajePercepcion] = useState('1');
  const [totalFise, setTotalFise] = useState('0');
  const [editarTotalAPagar, setEditarTotalAPagar] = useState(false);
  const [totalAPagarManual, setTotalAPagarManual] = useState('');

  const productos = catalogos.data?.productos ?? [];

  // La afectación se recuerda entre compras (el panel anterior la guardaba en
  // `localStorage` con el botón «Guardar configuración»).
  useEffect(() => {
    if (!abierto) return;

    const igv = localStorage.getItem('compra_afectacion_igv');
    const retencion = localStorage.getItem('compra_afectacion_retencion');
    const porcentaje = localStorage.getItem('compra_porcentaje_percepcion');

    if (igv) setAfectaIgv(igv === 'SI');
    if (retencion) setAfectaPercepcion(retencion === 'SI');
    if (porcentaje !== null) setPorcentajePercepcion(porcentaje);
  }, [abierto]);

  const guardarConfiguracion = () => {
    localStorage.setItem('compra_afectacion_igv', afectaIgv ? 'SI' : 'NO');
    localStorage.setItem('compra_afectacion_retencion', afectaPercepcion ? 'SI' : 'NO');
    localStorage.setItem('compra_porcentaje_percepcion', porcentajePercepcion);
    setAvisoConfig('Se pre-cargará la próxima vez que abras el formulario.');
  };

  const calculos = useMemo(() => {
    let gravadas = 0;
    let exoneradas = 0;
    let totalIgv = 0;
    let totalIsc = 0;
    let totalRodaje = 0;
    let totalImporte = 0;

    const detalle = lineas.map((linea) => {
      const cantidad = Number(linea.cantidad) || 0;
      const basico = Number(linea.basico) || 0;
      const dscto = Number(linea.dscto ?? 0) || 0;
      const isc = Number(linea.isc ?? 0) || 0;
      const rod = Number(linea.rod ?? 0) || 0;

      const valorVenta = (basico - dscto + isc + rod) * cantidad;
      const igv = afectaIgv ? valorVenta * 0.18 : 0;
      const unitario = cantidad > 0 ? (valorVenta + igv) / cantidad : 0;
      const importe = valorVenta + igv;

      if (cantidad > 0 && basico > 0) {
        if (afectaIgv) gravadas += valorVenta;
        else exoneradas += valorVenta;

        totalIgv += igv;
        totalIsc += isc * cantidad;
        totalRodaje += rod * cantidad;
        totalImporte += importe;
      }

      return { valorVenta, igv, unitario, importe };
    });

    const fise = Number(totalFise) || 0;
    const importeTotal = totalImporte + fise;
    const porcentaje = Number(porcentajePercepcion) || 0;
    const percepcion = afectaPercepcion ? importeTotal * (porcentaje / 100) : 0;

    return {
      detalle,
      gravadas,
      exoneradas,
      totalIgv,
      totalIsc,
      totalRodaje,
      totalImporte,
      fise,
      importeTotal,
      percepcion,
      totalAPagar: importeTotal + percepcion,
    };
  }, [lineas, afectaIgv, afectaPercepcion, porcentajePercepcion, totalFise]);

  const totalAPagarFinal = editarTotalAPagar ? Number(totalAPagarManual) || 0 : calculos.totalAPagar;

  const cambiarLinea = (clave: number, parcial: Partial<LineaEditable>) =>
    setLineas((previas) => previas.map((linea) => (linea.clave === clave ? { ...linea, ...parcial } : linea)));

  const enviar = () => {
    setError(null);

    const validas = lineas.filter((linea) => Number(linea.producto_id) > 0 && Number(linea.cantidad) > 0);

    if (validas.length === 0) {
      setError('Agrega al menos un producto a la compra.');

      return;
    }

    if (totalAPagarFinal <= 0) {
      setError('El total a pagar no puede ser 0 o menor.');

      return;
    }

    registrar.mutate(
      {
        ...cabecera,
        numero: Number(cabecera.numero),
        fecha_vencimiento: cabecera.fecha_vencimiento || null,
        total_fise: calculos.fise,
        total_igv: Number(calculos.totalIgv.toFixed(2)),
        total_icbper: 0,
        importe_total: Number(calculos.importeTotal.toFixed(2)),
        percepcion: Number(calculos.percepcion.toFixed(2)),
        total_a_pagar: Number(totalAPagarFinal.toFixed(2)),
        productos: lineas.map((linea, indice) => ({
          producto_id: Number(linea.producto_id),
          cantidad: Number(linea.cantidad) || 0,
          basico: Number(linea.basico) || 0,
          dscto: Number(linea.dscto ?? 0) || 0,
          isc: Number(linea.isc ?? 0) || 0,
          rod: Number(linea.rod ?? 0) || 0,
          valor_venta: Number((calculos.detalle[indice]?.valorVenta ?? 0).toFixed(2)),
          igv: Number((calculos.detalle[indice]?.igv ?? 0).toFixed(2)),
          precio_unidad: Number((calculos.detalle[indice]?.unitario ?? 0).toFixed(6)),
          importe: Number((calculos.detalle[indice]?.importe ?? 0).toFixed(2)),
        })),
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          onClose();
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar la compra.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="xl"
      title="Registrar compra"
      description="El stock no se mueve aquí: se carga después con la entrada de Inventario, que marca la línea como almacenada."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" loading={registrar.isPending} onClick={enviar}>
            Registrar compra
          </Button>
        </>
      }
    >
      {catalogos.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Tipo de comprobante</span>
            <Select
              className={CONTROL}
              value={cabecera.tipo_comprobante_id}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, tipo_comprobante_id: Number(evento.target.value) }))
              }
            >
              {(catalogos.data?.tipos_comprobante ?? []).map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Proveedor</span>
            <Select
              className={CONTROL}
              value={cabecera.proveedor_id}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, proveedor_id: Number(evento.target.value) }))
              }
            >
              <option value={0}>SELECCIONE</option>
              {(catalogos.data?.proveedores ?? []).map((proveedor) => (
                <option key={proveedor.id} value={proveedor.id}>
                  {proveedor.nombre} · {proveedor.ruc}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Afectación IGV</span>
            <Select
              className={CONTROL}
              value={afectaIgv ? 'SI' : 'NO'}
              onChange={(evento) => {
                setAfectaIgv(evento.target.value === 'SI');
                setAvisoConfig(null);
              }}
            >
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Afectación percepción</span>
            <Select
              className={CONTROL}
              value={afectaPercepcion ? 'SI' : 'NO'}
              onChange={(evento) => {
                setAfectaPercepcion(evento.target.value === 'SI');
                setAvisoConfig(null);
              }}
            >
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </Select>
          </label>

          {afectaPercepcion ? (
            <Input
              label="% Percepción"
              type="number"
              step="0.01"
              min={0}
              max={10}
              value={porcentajePercepcion}
              onChange={(evento) => {
                setPorcentajePercepcion(evento.target.value);
                setAvisoConfig(null);
              }}
            />
          ) : null}

          <div className="flex items-end">
            <Button variant="outline" size="sm" onClick={guardarConfiguracion}>
              Guardar configuración
            </Button>
          </div>

          <Input
            label="Serie"
            value={cabecera.serie}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, serie: evento.target.value }))}
          />
          <Input
            label="Número"
            type="number"
            value={cabecera.numero}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, numero: evento.target.value }))}
          />
          <Input
            label="Fecha de emisión"
            type="date"
            value={cabecera.fecha_emision}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, fecha_emision: evento.target.value }))}
          />
          <Input
            label="Fecha de vencimiento"
            type="date"
            value={cabecera.fecha_vencimiento}
            onChange={(evento) =>
              setCabecera((previa) => ({ ...previa, fecha_vencimiento: evento.target.value }))
            }
          />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Moneda</span>
            <Select
              className={CONTROL}
              value={cabecera.moneda}
              onChange={(evento) =>
                setCabecera((previa) => ({ ...previa, moneda: evento.target.value as 'SOLES' | 'DOLARES' }))
              }
            >
              <option value="SOLES">SOLES</option>
              <option value="DOLARES">DÓLARES</option>
            </Select>
          </label>
          <Input
            label="Tipo de cambio"
            type="number"
            step="0.001"
            value={cabecera.tipo_cambio}
            onChange={(evento) =>
              setCabecera((previa) => ({ ...previa, tipo_cambio: Number(evento.target.value) }))
            }
          />
          <Input
            label="Orden de compra"
            value={cabecera.orden_compra}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, orden_compra: evento.target.value }))}
          />
          <Input
            label="Guía"
            value={cabecera.guia}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, guia: evento.target.value }))}
          />
          <Input
            label="SCOP"
            value={cabecera.scop}
            onChange={(evento) => setCabecera((previa) => ({ ...previa, scop: evento.target.value }))}
          />
        </div>

        <section className="rounded-card border border-ink-100 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink-800">Detalle de compra</h3>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Plus className="size-4" />}
              onClick={() =>
                setLineas((previas) => [
                  ...previas,
                  {
                    clave: (previas.at(-1)?.clave ?? 0) + 1,
                    producto_id: 0,
                    cantidad: 1,
                    basico: 0,
                    dscto: 0,
                    isc: 0,
                    rod: 0,
                  },
                ])
              }
            >
              Agregar producto
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Cantidad</th>
                  <th className="min-w-[13rem] px-2 py-2">Descripción</th>
                  <th className="px-2 py-2">Básico</th>
                  <th className="px-2 py-2">Dscto.</th>
                  <th className="px-2 py-2">ISC</th>
                  <th className="px-2 py-2">ROD</th>
                  <th className="px-2 py-2 text-right">IGV</th>
                  <th className="px-2 py-2 text-right">P. Unit</th>
                  <th className="px-2 py-2 text-right">Importe</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {lineas.map((linea, indice) => (
                  <tr key={linea.clave} className="border-t border-ink-100">
                    <td className="px-2 py-2">
                      <input
                        className={CONTROL_INLINE}
                        type="number"
                        step="0.001"
                        min={0}
                        value={linea.cantidad}
                        onChange={(evento) =>
                          cambiarLinea(linea.clave, { cantidad: Number(evento.target.value) })
                        }
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Select
                        className={CONTROL}
                        value={linea.producto_id}
                        searchable
                        onChange={(evento) => {
                          const elegido = productos.find((item) => item.id === Number(evento.target.value));

                          cambiarLinea(linea.clave, {
                            producto_id: Number(evento.target.value),
                            basico: Number(elegido?.precio_compra ?? linea.basico ?? 0),
                          });
                        }}
                      >
                        <option value={0}>SELECCIONE</option>
                        {productos.map((producto) => (
                          <option key={producto.id} value={producto.id}>
                            {producto.nombre_item_api ?? producto.nombre}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        className={CONTROL_INLINE}
                        type="number"
                        step="0.000001"
                        min={0}
                        value={linea.basico}
                        onChange={(evento) =>
                          cambiarLinea(linea.clave, { basico: Number(evento.target.value) })
                        }
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        className={CONTROL_INLINE}
                        type="number"
                        step="0.000001"
                        min={0}
                        value={linea.dscto ?? 0}
                        onChange={(evento) =>
                          cambiarLinea(linea.clave, { dscto: Number(evento.target.value) })
                        }
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        className={CONTROL_INLINE}
                        type="number"
                        step="0.000001"
                        min={0}
                        value={linea.isc ?? 0}
                        onChange={(evento) => cambiarLinea(linea.clave, { isc: Number(evento.target.value) })}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        className={CONTROL_INLINE}
                        type="number"
                        step="0.000001"
                        min={0}
                        value={linea.rod ?? 0}
                        onChange={(evento) => cambiarLinea(linea.clave, { rod: Number(evento.target.value) })}
                      />
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-ink-700">
                      {monto(calculos.detalle[indice]?.igv ?? 0)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-ink-700">
                      {(calculos.detalle[indice]?.unitario ?? 0).toLocaleString('es-PE', {
                        maximumFractionDigits: 6,
                      })}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold text-ink-900">
                      {monto(calculos.detalle[indice]?.importe ?? 0)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Quitar la línea"
                        disabled={lineas.length === 1}
                        onClick={() =>
                          setLineas((previas) => previas.filter((item) => item.clave !== linea.clave))
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <dl>
              {[
                { etiqueta: 'Total Op. Gravadas', valor: monto(calculos.gravadas) },
                { etiqueta: 'Total Op. Exoneradas', valor: monto(calculos.exoneradas) },
                { etiqueta: 'Total ISC', valor: monto(calculos.totalIsc) },
                { etiqueta: 'Total Rodaje', valor: monto(calculos.totalRodaje) },
                { etiqueta: 'Total IGV', valor: monto(calculos.totalIgv) },
                { etiqueta: 'Total ICBPER', valor: monto(0) },
              ].map((fila) => (
                <div
                  key={fila.etiqueta}
                  className="flex items-center justify-between gap-3 border-t border-ink-100 py-1.5"
                >
                  <dt className="text-sm text-ink-600">{fila.etiqueta}</dt>
                  <dd className="text-sm tabular-nums text-ink-800">{fila.valor}</dd>
                </div>
              ))}
            </dl>

            <dl>
              <div className="flex items-center justify-between gap-3 border-t border-ink-100 py-1.5">
                <dt className="text-sm text-ink-600">Total FISE</dt>
                <dd>
                  <input
                    className={CONTROL_INLINE}
                    type="number"
                    step="0.01"
                    value={totalFise}
                    onChange={(evento) => setTotalFise(evento.target.value)}
                  />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-ink-100 py-1.5">
                <dt className="text-sm text-ink-600">Importe Total</dt>
                <dd className="text-sm tabular-nums text-ink-800">{monto(calculos.importeTotal)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-ink-100 py-1.5">
                <dt className="text-sm text-ink-600">Percepción</dt>
                <dd className="text-sm tabular-nums text-ink-800">{monto(calculos.percepcion)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-ink-100 py-1.5">
                <label className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <input
                    type="checkbox"
                    checked={editarTotalAPagar}
                    onChange={(evento) => {
                      setEditarTotalAPagar(evento.target.checked);
                      setTotalAPagarManual(calculos.totalAPagar.toFixed(2));
                    }}
                  />
                  TOTAL A PAGAR
                </label>
                {editarTotalAPagar ? (
                  <input
                    className={CONTROL_INLINE}
                    type="number"
                    step="0.01"
                    value={totalAPagarManual}
                    onChange={(evento) => setTotalAPagarManual(evento.target.value)}
                  />
                ) : (
                  <dd className="text-sm font-bold tabular-nums text-brand-700">
                    {monto(totalAPagarFinal)}
                  </dd>
                )}
              </div>
            </dl>
          </div>
        </section>

        {avisoConfig ? (
          <Alert variant="success" title="Configuración guardada">
            {avisoConfig}
          </Alert>
        ) : null}

        {error ? (
          <Alert variant="error" title="No se pudo registrar">
            {error}
          </Alert>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Pagar al proveedor
 * ------------------------------------------------------------------ */

export function CompraPagoDialog({ abierto, onAviso, onClose }: AvisoProps & { abierto: boolean }) {
  const resumen = useResumenPagos(abierto);
  const [proveedorId, setProveedorId] = useState<number | null>(null);
  const detalle = usePagosDeProveedor(proveedorId);
  const registrar = useRegistrarPago();
  const anular = useAnularPago();

  const [reparto, setReparto] = useState<Record<number, number>>({});
  const [pago, setPago] = useState({
    account_id: 0,
    payment_method_id: 0,
    fecha_pago: hoyIso(),
    referencia: '',
    informacion_adicional: '',
    monto_pagado: 0,
    comprobante: null as { nombre: string; contenido_base64: string } | null,
  });
  const [porAnular, setPorAnular] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const compras = detalle.data?.compras ?? [];
  const seleccionado = Object.values(reparto).reduce((suma, valor) => suma + valor, 0);

  const alternar = (compra: CompraPorPagar) =>
    setReparto((previo) => {
      const copia = { ...previo };

      if (copia[compra.id] === undefined) copia[compra.id] = Number(compra.pendiente_pago);
      else delete copia[compra.id];

      return copia;
    });

  const enviar = () => {
    setError(null);

    if (proveedorId === null) return;

    const afectaciones: AfectacionPago[] = Object.entries(reparto).map(([id, monto]) => ({
      id_compra: Number(id),
      monto: Number(monto),
    }));

    registrar.mutate(
      {
        proveedor_id: proveedorId,
        account_id: pago.account_id,
        payment_method_id: pago.payment_method_id,
        monto_seleccionado: seleccionado,
        monto_pagado: Number(pago.monto_pagado) || seleccionado,
        fecha_pago: pago.fecha_pago,
        referencia: pago.referencia,
        informacion_adicional: pago.informacion_adicional,
        json_pagos_afectacion: afectaciones,
        comprobante: pago.comprobante,
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          setReparto({});
          setPago((previo) => ({ ...previo, referencia: '', monto_pagado: 0, comprobante: null }));
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el pago.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="xl"
      title="Pagar al proveedor"
      description="El pago se reparte entre las compras elegidas y deja el movimiento bancario del tipo «Pago de compras»."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo registrar el pago">
          {error}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Proveedor con saldo</span>
          <Select
            className={CONTROL}
            value={proveedorId ?? 0}
            onChange={(evento) => {
              const id = Number(evento.target.value);

              setProveedorId(id || null);
              setReparto({});
            }}
          >
            <option value={0}>SELECCIONE</option>
            {(resumen.data?.proveedores ?? []).map((item) => (
              <option key={item.proveedor_id} value={item.proveedor_id}>
                {item.proveedor} · pendiente {monto(item.pendiente)}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Cuenta bancaria</span>
          <Select
            className={CONTROL}
            value={pago.account_id}
            onChange={(evento) => setPago((previo) => ({ ...previo, account_id: Number(evento.target.value) }))}
          >
            <option value={0}>SELECCIONE</option>
            {(detalle.data?.cuentas ?? resumen.data?.cuentas ?? []).map((cuenta) => (
              <option key={cuenta.id} value={cuenta.id}>
                {cuenta.banco ?? 'Banco'} · {cuenta.description ?? ''} {cuenta.number ?? ''}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Método de pago</span>
          <Select
            className={CONTROL}
            value={pago.payment_method_id}
            onChange={(evento) =>
              setPago((previo) => ({ ...previo, payment_method_id: Number(evento.target.value) }))
            }
          >
            <option value={0}>SELECCIONE</option>
            {(detalle.data?.metodos ?? resumen.data?.metodos ?? []).map((metodo) => (
              <option key={metodo.id} value={metodo.id}>
                {metodo.nombre}
              </option>
            ))}
          </Select>
        </label>

        <Input
          label="Fecha de pago"
          type="date"
          value={pago.fecha_pago}
          onChange={(evento) => setPago((previo) => ({ ...previo, fecha_pago: evento.target.value }))}
        />
        <Input
          label="Referencia"
          value={pago.referencia}
          onChange={(evento) => setPago((previo) => ({ ...previo, referencia: evento.target.value }))}
        />
        <Input
          label="Información adicional"
          value={pago.informacion_adicional}
          onChange={(evento) =>
            setPago((previo) => ({ ...previo, informacion_adicional: evento.target.value }))
          }
        />
        <Input
          label="Importe pagado (S/)"
          type="number"
          step="0.01"
          value={pago.monto_pagado}
          onChange={(evento) => setPago((previo) => ({ ...previo, monto_pagado: Number(evento.target.value) }))}
        />
        <ArchivoInput
          className="sm:col-span-2 lg:col-span-3"
          label="Comprobante del pago (PDF, JPG o PNG)"
          hint="Se adjunta al pago registrado y se puede ver desde la lista de pagos."
          nombre={pago.comprobante?.nombre ?? null}
          onSeleccionar={(archivo) => setPago((previo) => ({ ...previo, comprobante: archivo }))}
        />
      </div>

      {proveedorId === null ? (
        <Alert className="mt-4" variant="info" title="Elige un proveedor">
          <span className={ICONO_EN_ALERTA}>
            El listado sólo muestra proveedores con compras registradas; las compras por pagar y sus
            pagos aparecen al elegirlo.
          </span>
        </Alert>
      ) : null}

      {detalle.isLoading ? <Skeleton className="mt-4 h-40 w-full" /> : null}

      {compras.length > 0 ? (
        <section className="mt-4 rounded-card border border-ink-100 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink-800">
            Compras por pagar ({compras.length}) · pendiente {monto(detalle.data?.totales.pendiente ?? 0)} ·
            vencido {monto(detalle.data?.totales.vencido ?? 0)}
          </h3>

          <div className="max-h-[35vh] overflow-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Pagar</th>
                  <th className="px-2 py-2">Comprobante</th>
                  <th className="px-2 py-2">Emisión / vencimiento</th>
                  <th className="px-2 py-2 text-right">Total</th>
                  <th className="px-2 py-2 text-right">Pendiente</th>
                  <th className="px-2 py-2 text-right">Importe a aplicar</th>
                </tr>
              </thead>
              <tbody>
                {compras.map((compra) => (
                  <tr key={compra.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        className="size-4 accent-brand-500"
                        checked={reparto[compra.id] !== undefined}
                        onChange={() => alternar(compra)}
                        aria-label={`Pagar la compra ${compra.comprobante}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <p className="font-semibold text-ink-900">{compra.comprobante}</p>
                      <p className="text-xs text-ink-500">{compra.tipo_comprobante}</p>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {fechaCorta(compra.fecha_emision)}
                      <p className="text-xs text-ink-500">vence {fechaCorta(compra.fecha_vencimiento)}</p>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">{monto(compra.total_a_pagar)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {monto(compra.pendiente_pago)}
                      {Number(compra.dias_vencido) > 0 ? (
                        <p className="text-xs text-rose-600">{compra.dias_vencido} días vencida</p>
                      ) : null}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input
                        className="h-9 w-28 rounded-control border border-ink-200 px-2 text-right text-sm"
                        type="number"
                        step="0.01"
                        disabled={reparto[compra.id] === undefined}
                        value={reparto[compra.id] ?? 0}
                        onChange={(evento) =>
                          setReparto((previo) => ({ ...previo, [compra.id]: Number(evento.target.value) }))
                        }
                        aria-label={`Importe a aplicar a ${compra.comprobante}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-600">
              Seleccionado: <strong className="text-ink-900">{monto(seleccionado)}</strong>
            </p>
            <Button
              size="sm"
              loading={registrar.isPending}
              disabled={!seleccionado || !pago.account_id || !pago.payment_method_id}
              onClick={enviar}
            >
              Registrar pago
            </Button>
          </div>
        </section>
      ) : null}

      {detalle.data?.pagos.length ? (
        <section className="mt-4 rounded-card border border-ink-100 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink-800">
            Pagos registrados ({detalle.data.pagos.length})
          </h3>

          <ul className="space-y-2">
            {detalle.data.pagos.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ink-100 p-3"
              >
                <div>
                  <p className="font-semibold text-ink-900">
                    {monto(item.monto_pagado)} · {fechaCorta(item.fecha_pago)}
                  </p>
                  <p className="text-xs text-ink-500">
                    {item.banco ?? 'Sin banco'}
                    {item.referencia ? ` · ref. ${item.referencia}` : ''}
                    {item.afectaciones?.length
                      ? ` · ${item.afectaciones.length} compra${item.afectaciones.length === 1 ? '' : 's'}`
                      : ''}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {item.archivo ? (
                    <a
                      className="text-xs font-semibold text-brand-700 underline"
                      href={`/${item.archivo}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Comprobante
                    </a>
                  ) : null}
                  <Button variant="ghost" size="sm" onClick={() => setPorAnular(item.id)}>
                    Anular
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ConfirmDialog
        open={porAnular !== null}
        title="Anular el pago"
        description="El importe vuelve a quedar pendiente en las compras afectadas y su movimiento bancario se anula."
        confirmLabel="Anular pago"
        loading={anular.isPending}
        onClose={() => setPorAnular(null)}
        onConfirm={() => {
          const id = porAnular;

          setPorAnular(null);

          if (id === null) return;

          anular.mutate(id, {
            onSuccess: (respuesta) => onAviso(respuesta.mensaje),
            onError: (fallo) =>
              setError(fallo instanceof ApiError ? fallo.message : 'No se pudo anular el pago.'),
          });
        }}
      />
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Notas de crédito y débito
 * ------------------------------------------------------------------ */

export function CompraNotaDialog({
  compraId,
  total,
  onClose,
  onAviso,
}: AvisoProps & {
  /** Compra a la que se le añade la nota (`null` = cerrado). */
  compraId: number | null;
  /** Total de las líneas de la compra (tope de las notas de crédito). */
  total: number;
}) {
  const consulta = useNotasDeCompra(compraId);
  const guardar = useGuardarNota(compraId);
  const anular = useAnularNota();

  const notas = consulta.data?.notas ?? [];

  const [nota, setNota] = useState({
    nota_id: undefined as number | undefined,
    tipo: 'credito' as 'credito' | 'debito',
    monto: '',
    motivo: '',
    serie: '',
    numero: '',
    fecha_emision: hoyIso(),
    aplica_igv: true,
  });
  const [porAnular, setPorAnular] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const creditos = notas
    .filter((item) => item.tipo === 'credito' && Number(item.estado) === 1)
    .reduce((suma, item) => suma + Number(item.monto), 0);

  const enviar = () => {
    setError(null);
    guardar.mutate(
      {
        nota_id: nota.nota_id,
        tipo: nota.tipo,
        monto: Number(nota.monto),
        motivo: nota.motivo,
        serie: nota.serie,
        numero: nota.numero,
        fecha_emision: nota.fecha_emision,
        aplica_igv: nota.aplica_igv,
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          setNota({
            nota_id: undefined,
            tipo: 'credito',
            monto: '',
            motivo: '',
            serie: '',
            numero: '',
            fecha_emision: hoyIso(),
            aplica_igv: true,
          });
        },
        onError: (fallo) => setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar la nota.'),
      },
    );
  };

  return (
    <Dialog
      open={compraId !== null}
      onClose={onClose}
      size="xl"
      title="Notas de crédito y débito"
      description="La nota de crédito sube lo pagado y baja el pendiente; la de débito hace lo contrario."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <Alert variant="info" title="Tope de las notas de crédito">
        <span className={ICONO_EN_ALERTA}>
          Las notas de crédito no pueden pasar del total de las líneas de la compra (
          {monto(total)}); ya hay {monto(creditos)} en notas activas. Las de débito no tienen tope.
        </span>
      </Alert>

      {error ? (
        <Alert className="mt-4" variant="error" title="No se pudo guardar la nota">
          {error}
        </Alert>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Tipo</span>
          <Select
            className={CONTROL}
            value={nota.tipo}
            onChange={(evento) =>
              setNota((previa) => ({ ...previa, tipo: evento.target.value as 'credito' | 'debito' }))
            }
          >
            <option value="credito">Crédito</option>
            <option value="debito">Débito</option>
          </Select>
        </label>
        <Input
          label="Monto (S/)"
          type="number"
          step="0.01"
          value={nota.monto}
          onChange={(evento) => setNota((previa) => ({ ...previa, monto: evento.target.value }))}
        />
        <Input
          label="Fecha de emisión"
          type="date"
          value={nota.fecha_emision}
          onChange={(evento) => setNota((previa) => ({ ...previa, fecha_emision: evento.target.value }))}
        />
        <Input
          label="Serie"
          value={nota.serie}
          onChange={(evento) => setNota((previa) => ({ ...previa, serie: evento.target.value }))}
        />
        <Input
          label="Número"
          value={nota.numero}
          onChange={(evento) => setNota((previa) => ({ ...previa, numero: evento.target.value }))}
        />
        <Input
          label="Motivo"
          value={nota.motivo}
          onChange={(evento) => setNota((previa) => ({ ...previa, motivo: evento.target.value }))}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-brand-500"
            checked={nota.aplica_igv}
            onChange={(evento) => setNota((previa) => ({ ...previa, aplica_igv: evento.target.checked }))}
          />
          <span className="font-medium text-ink-700">Aplica IGV</span>
        </label>

        <div className="flex items-center gap-2">
          <Button size="sm" loading={guardar.isPending} onClick={enviar}>
            {nota.nota_id === undefined ? 'Añadir nota' : 'Guardar nota'}
          </Button>
          {nota.nota_id !== undefined ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNota((previa) => ({ ...previa, nota_id: undefined, monto: '', numero: '' }))}
            >
              Nueva
            </Button>
          ) : null}
        </div>
      </div>

      {consulta.isLoading ? <Skeleton className="mt-4 h-32 w-full" /> : null}

      <ul className="mt-4 space-y-2 empty:hidden">
        {notas.map((item) => (
          <li
            key={item.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ink-100 p-3"
          >
            <div>
              <p className="font-semibold text-ink-900">
                {item.tipo === 'credito' ? 'Crédito' : 'Débito'}{' '}
                {item.serie ? `${item.serie}-${item.numero ?? ''}` : item.comprobante} · {monto(item.monto)}
              </p>
              <p className="text-xs text-ink-500">
                {fechaCorta(item.fecha_emision)}
                {item.motivo ? ` · ${item.motivo}` : ''}
                {item.aplica_igv ? ' · aplica IGV' : ''}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={Number(item.estado) === 1 ? 'success' : 'neutral'}>
                {Number(item.estado) === 1 ? 'ACTIVA' : 'ANULADA'}
              </Badge>
              {Number(item.estado) === 1 ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setNota({
                        nota_id: item.id,
                        tipo: item.tipo,
                        monto: String(item.monto),
                        motivo: item.motivo ?? '',
                        serie: item.serie ?? item.comprobante.split('-')[0] ?? '',
                        numero: item.numero ?? item.comprobante.split('-')[1] ?? '',
                        fecha_emision: String(item.fecha_emision).slice(0, 10),
                        aplica_igv: Boolean(item.aplica_igv),
                      })
                    }
                  >
                    Editar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setPorAnular(item.id)}>
                    Anular
                  </Button>
                </>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {notas.length === 0 ? (
        <p className="mt-4 rounded-card border border-dashed border-ink-200 p-6 text-center text-sm text-ink-500">
          Sin notas asociadas.
        </p>
      ) : null}

      <ConfirmDialog
        open={porAnular !== null}
        title="Anular la nota"
        description="Se devuelven a la compra los importes que la nota había movido."
        confirmLabel="Anular nota"
        loading={anular.isPending}
        onClose={() => setPorAnular(null)}
        onConfirm={() => {
          const id = porAnular;

          setPorAnular(null);

          if (id === null) return;

          anular.mutate(id, {
            onSuccess: (respuesta) => onAviso(respuesta.mensaje),
            onError: (fallo) =>
              setError(fallo instanceof ApiError ? fallo.message : 'No se pudo anular la nota.'),
          });
        }}
      />
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Editar la compra y adjuntar su comprobante
 * ------------------------------------------------------------------ */

/**
 * Ventana de edición de una compra (`Compra::update` + `Compra::actualizar_compra_completa`).
 *
 * Se editan los datos del **comprobante** —tipo, serie, número, fechas, proveedor,
 * moneda y tipo de cambio— y las **líneas** (cantidad, básico, descuento, ISC y
 * rodaje), recalculando los importes con la afectación de IGV (18%) y de
 * percepción (1%). También se adjunta la **foto del comprobante**
 * (`uploads/compras/compra_<id>_<timestamp>.<ext>`).
 */
export function CompraEditarDialog({
  compra,
  onClose,
  onAviso,
}: AvisoProps & {
  /** Compra a editar; `null` = cerrado. */
  compra: {
    id: number;
    serie: string | null;
    numero: number | null;
    tipo_comprobante_id?: number;
    proveedor_id: number | null;
    fecha_emision: string | null;
    fecha_vencimiento: string | null;
    moneda: string | null;
    tipo_cambio: number | null;
    orden_compra: string | null;
    guia: string | null;
    scop: string | null;
    archivo: string | null;
  } | null;
}) {
  const catalogos = useCatalogosCompra(compra !== null);
  const guardar = useActualizarCompra(compra?.id ?? null);
  const subir = useSubirComprobanteCompra(compra?.id ?? null);

  const [datos, setDatos] = useState(() => ({
    tipo_comprobante_id: 2,
    serie: '',
    numero: '',
    proveedor_id: 0,
    fecha_emision: hoyIso(),
    fecha_vencimiento: '',
    moneda: 'SOLES' as 'SOLES' | 'DOLARES',
    tipo_cambio: 1,
    orden_compra: '',
    guia: '',
    scop: '',
    archivo: null as string | null,
  }));
  const [firma, setFirma] = useState('');
  const [firmaLineas, setFirmaLineas] = useState('');
  const [error, setError] = useState<string | null>(null);
  const detalle = useCompra(compra?.id ?? null);
  const [lineas, setLineas] = useState<LineaEditableCompra[]>([]);
  const [afectaIgv, setAfectaIgv] = useState(true);
  const [afectaPercepcion, setAfectaPercepcion] = useState(false);
  const [totalFise, setTotalFise] = useState('0');

  // La compra rellena el formulario en el render (guarda `firma`), sin efectos.
  if (compra && firma !== String(compra.id)) {
    setFirma(String(compra.id));
    setDatos({
      tipo_comprobante_id: compra.tipo_comprobante_id ?? 2,
      serie: compra.serie ?? '',
      numero: compra.numero === null ? '' : String(compra.numero),
      proveedor_id: compra.proveedor_id ?? 0,
      fecha_emision: String(compra.fecha_emision ?? '').slice(0, 10),
      fecha_vencimiento: String(compra.fecha_vencimiento ?? '').slice(0, 10),
      moneda: compra.moneda === 'DOLARES' ? 'DOLARES' : 'SOLES',
      tipo_cambio: compra.tipo_cambio ?? 1,
      orden_compra: compra.orden_compra ?? '',
      guia: compra.guia ?? '',
      scop: compra.scop ?? '',
      archivo: compra.archivo ?? null,
    });
    setError(null);
  }

  // Las líneas y los totales llegan con el detalle de la compra (segunda consulta).
  if (compra && detalle.data && firmaLineas !== String(compra.id)) {
    setFirmaLineas(String(compra.id));
    setLineas(lineasDeDetalle(detalle.data.productos));
    setTotalFise(String(detalle.data.compra.total_fise ?? 0));
    setAfectaIgv(Number(detalle.data.compra.total_igv ?? 0) > 0);
    setAfectaPercepcion(Number(detalle.data.compra.percepcion ?? 0) > 0);
  }

  const totales = useMemo(
    () => calcularTotales(lineas, afectaIgv, afectaPercepcion, totalFise),
    [lineas, afectaIgv, afectaPercepcion, totalFise],
  );

  const actualizarLinea = (
    id: number,
    campo: 'cantidad' | 'basico' | 'dscto' | 'isc' | 'rod',
    valor: string,
  ) => {
    setLineas((previo) =>
      previo.map((linea) =>
        linea.id === id ? { ...linea, [campo]: valor === '' ? 0 : Number(valor) } : linea,
      ),
    );
  };

  const enviar = () => {
    setError(null);
    guardar.mutate(
      {
        ...datos,
        numero: Number(datos.numero),
        fecha_vencimiento: datos.fecha_vencimiento || null,
        afectacion_igv: afectaIgv ? 'SI' : 'NO',
        afectacion_percepcion: afectaPercepcion ? 'SI' : 'NO',
        total_fise: totales.fise,
        total_igv: totales.igv,
        importe_total: totales.importeTotal,
        percepcion: totales.percepcion,
        total_a_pagar: totales.totalAPagar,
        productos: lineas.map((linea) => ({
          compra_producto_id: linea.id,
          producto_id: linea.producto_id,
          cantidad: linea.cantidad,
          basico: linea.basico,
          dscto: linea.dscto,
          isc: linea.isc,
          rod: linea.rod,
        })),
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          onClose();
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo actualizar la compra.'),
      },
    );
  };

  return (
    <Dialog
      open={compra !== null}
      onClose={onClose}
      size="lg"
      title={`Editar la compra ${compra ? `${compra.serie ?? ''}-${compra.numero ?? ''}` : ''}`}
      description="Corrige el comprobante y sus líneas; los importes y el total a pagar se recalculan al guardar."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardar.isPending}
            disabled={detalle.isLoading}
            onClick={enviar}
          >
            Guardar cambios
          </Button>
        </>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo actualizar">
          {error}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Tipo de comprobante</span>
          <Select
            className={CONTROL}
            value={datos.tipo_comprobante_id}
            onChange={(evento) =>
              setDatos((previo) => ({ ...previo, tipo_comprobante_id: Number(evento.target.value) }))
            }
          >
            {(catalogos.data?.tipos_comprobante ?? []).map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.nombre}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Proveedor</span>
          <Select
            className={CONTROL}
            value={datos.proveedor_id}
            onChange={(evento) =>
              setDatos((previo) => ({ ...previo, proveedor_id: Number(evento.target.value) }))
            }
          >
            <option value={0}>SELECCIONE</option>
            {(catalogos.data?.proveedores ?? []).map((proveedor) => (
              <option key={proveedor.id} value={proveedor.id}>
                {proveedor.nombre} · {proveedor.ruc}
              </option>
            ))}
          </Select>
        </label>

        <Input
          label="Serie"
          value={datos.serie}
          onChange={(evento) => setDatos((previo) => ({ ...previo, serie: evento.target.value }))}
        />
        <Input
          label="Número"
          type="number"
          value={datos.numero}
          onChange={(evento) => setDatos((previo) => ({ ...previo, numero: evento.target.value }))}
        />
        <Input
          label="Fecha de emisión"
          type="date"
          value={datos.fecha_emision}
          onChange={(evento) => setDatos((previo) => ({ ...previo, fecha_emision: evento.target.value }))}
        />
        <Input
          label="Fecha de vencimiento"
          type="date"
          value={datos.fecha_vencimiento}
          onChange={(evento) =>
            setDatos((previo) => ({ ...previo, fecha_vencimiento: evento.target.value }))
          }
        />
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Moneda</span>
          <Select
            className={CONTROL}
            value={datos.moneda}
            onChange={(evento) =>
              setDatos((previo) => ({ ...previo, moneda: evento.target.value as 'SOLES' | 'DOLARES' }))
            }
          >
            <option value="SOLES">SOLES</option>
            <option value="DOLARES">DÓLARES</option>
          </Select>
        </label>
        <Input
          label="Tipo de cambio"
          type="number"
          step="0.001"
          value={datos.tipo_cambio}
          onChange={(evento) => setDatos((previo) => ({ ...previo, tipo_cambio: Number(evento.target.value) }))}
        />
        <Input
          label="Orden de compra"
          value={datos.orden_compra}
          onChange={(evento) => setDatos((previo) => ({ ...previo, orden_compra: evento.target.value }))}
        />
        <Input
          label="Guía"
          value={datos.guia}
          onChange={(evento) => setDatos((previo) => ({ ...previo, guia: evento.target.value }))}
        />
        <Input
          label="SCOP"
          value={datos.scop}
          onChange={(evento) => setDatos((previo) => ({ ...previo, scop: evento.target.value }))}
        />
      </div>

      <section className="mt-4 rounded-card border border-ink-100 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-ink-800">Detalle de productos</h3>

        {detalle.isLoading ? <Skeleton className="h-24 w-full" /> : null}

        {detalle.isError ? (
          <Alert variant="error" title="No se pudieron cargar las líneas">
            No se pudieron cargar las líneas de la compra.
          </Alert>
        ) : null}

        {!detalle.isLoading && !detalle.isError ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Producto</th>
                  <th className="px-2 py-2">Cantidad</th>
                  <th className="px-2 py-2">Básico</th>
                  <th className="px-2 py-2">Dscto.</th>
                  <th className="px-2 py-2">ISC</th>
                  <th className="px-2 py-2">ROD</th>
                  <th className="px-2 py-2 text-right">IGV</th>
                  <th className="px-2 py-2 text-right">P. Unit</th>
                  <th className="px-2 py-2 text-right">Importe</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((linea) => {
                  const valorVenta =
                    (linea.basico - linea.dscto + linea.isc + linea.rod) * linea.cantidad;
                  const igvLinea = afectaIgv ? valorVenta * 0.18 : 0;
                  const importeLinea = valorVenta + igvLinea;
                  const unitario = linea.cantidad > 0 ? importeLinea / linea.cantidad : 0;

                  return (
                    <tr key={linea.id} className="border-t border-ink-100">
                      <td
                        className="max-w-[12rem] truncate px-2 py-2 font-medium text-ink-800"
                        title={linea.producto}
                      >
                        {linea.producto}
                      </td>
                      <td className="px-2 py-2">
                        <input
                          className={CONTROL_INLINE}
                          type="number"
                          step="0.001"
                          value={linea.cantidad}
                          onChange={(evento) =>
                            actualizarLinea(linea.id, 'cantidad', evento.target.value)
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          className={CONTROL_INLINE}
                          type="number"
                          step="0.000001"
                          value={linea.basico}
                          onChange={(evento) =>
                            actualizarLinea(linea.id, 'basico', evento.target.value)
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          className={CONTROL_INLINE}
                          type="number"
                          step="0.000001"
                          value={linea.dscto}
                          onChange={(evento) =>
                            actualizarLinea(linea.id, 'dscto', evento.target.value)
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          className={CONTROL_INLINE}
                          type="number"
                          step="0.000001"
                          value={linea.isc}
                          onChange={(evento) => actualizarLinea(linea.id, 'isc', evento.target.value)}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          className={CONTROL_INLINE}
                          type="number"
                          step="0.000001"
                          value={linea.rod}
                          onChange={(evento) => actualizarLinea(linea.id, 'rod', evento.target.value)}
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-ink-700">
                        {monto(igvLinea)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-ink-700">
                        {unitario.toLocaleString('es-PE', { maximumFractionDigits: 6 })}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold text-ink-900">
                        {monto(importeLinea)}
                      </td>
                    </tr>
                  );
                })}

                {lineas.length === 0 ? (
                  <tr className="border-t border-ink-100">
                    <td colSpan={9} className="px-3 py-6 text-center text-sm text-ink-500">
                      La compra no tiene líneas.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="mt-4 rounded-card border border-ink-100 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-ink-800">Resumen de cálculos</h3>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Afectación IGV</span>
            <Select
              className={CONTROL}
              value={afectaIgv ? 'SI' : 'NO'}
              onChange={(evento) => setAfectaIgv(evento.target.value === 'SI')}
            >
              <option value="SI">SI (18%)</option>
              <option value="NO">NO</option>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Afectación percepción</span>
            <Select
              className={CONTROL}
              value={afectaPercepcion ? 'SI' : 'NO'}
              onChange={(evento) => setAfectaPercepcion(evento.target.value === 'SI')}
            >
              <option value="SI">SI (1%)</option>
              <option value="NO">NO</option>
            </Select>
          </label>

          <Input
            label="Total FISE"
            type="number"
            step="0.01"
            value={totalFise}
            onChange={(evento) => setTotalFise(evento.target.value)}
          />
        </div>

        <dl className="mt-3">
          {[
            { etiqueta: 'Importe de las líneas', valor: monto(totales.importe) },
            { etiqueta: 'Total IGV', valor: monto(totales.igv) },
            { etiqueta: 'Importe total', valor: monto(totales.importeTotal) },
            { etiqueta: 'Percepción (1%)', valor: monto(totales.percepcion) },
            { etiqueta: 'TOTAL A PAGAR', valor: monto(totales.totalAPagar), fuerte: true },
          ].map((fila) => (
            <div
              key={fila.etiqueta}
              className="flex items-center justify-between gap-3 border-t border-ink-100 py-1.5"
            >
              <dt className={fila.fuerte ? 'text-sm font-semibold text-ink-900' : 'text-sm text-ink-600'}>
                {fila.etiqueta}
              </dt>
              <dd
                className={
                  fila.fuerte
                    ? 'text-sm font-bold tabular-nums text-ink-900'
                    : 'text-sm tabular-nums text-ink-800'
                }
              >
                {fila.valor}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="rounded-card border border-ink-100 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-ink-800">Foto del comprobante</h3>

        <p className="mb-2 text-xs text-ink-500">
          Se guarda en <code>uploads/compras/</code> como{' '}
          <code>compra_{String(compra?.id ?? 0).padStart(8, '0')}_&lt;fecha&gt;.&lt;ext&gt;</code>, igual que el
          panel anterior.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <ArchivoInput
            className="min-w-[16rem] flex-1"
            nombre={datos.archivo ? (datos.archivo.split('/').pop() ?? datos.archivo) : null}
            placeholder="Sin comprobante adjunto"
            onSeleccionar={(archivo) => {
              if (!archivo) return;

              setError(null);

              subir.mutate(archivo, {
                onSuccess: (respuesta) => {
                  setDatos((previo) => ({ ...previo, archivo: respuesta.archivo }));
                  onAviso(respuesta.mensaje);
                },
                onError: (fallo) =>
                  setError(
                    fallo instanceof ApiError ? fallo.message : 'No se pudo guardar el comprobante.',
                  ),
              });
            }}
          />

          {datos.archivo ? (
            <a
              className="text-sm font-semibold text-brand-700 underline"
              href={`/${datos.archivo}`}
              target="_blank"
              rel="noreferrer"
            >
              Ver el comprobante
            </a>
          ) : (
            <span className="text-xs text-ink-500">Sin comprobante adjunto.</span>
          )}
        </div>
      </section>
    </Dialog>
  );
}
